import React from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { formFieldStyles } from "./styles";
import { LabelWithTooltip } from "./LabelWithTooltip";

interface FormFieldProps extends WithStyles<typeof formFieldStyles> {
	/** Ties the label to the input, so clicking the label focuses it. */
	id?: string;
	label: string;
	/** Keeps the label for screen readers but not on screen, where the copy above already names the field. */
	hideLabel?: boolean;
	value: string;
	onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
	onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
	placeholder?: string;
	required?: boolean;
	error?: boolean;
	helperText?: string;
	/** Guidance under the field in the neutral text colour, e.g. how to use it. `helperText` replaces it. */
	hint?: string;
	type?: string;
	/** Bounds for a number field: they limit its stepper, they do not validate. */
	min?: number;
	max?: number;
	/** Fixed text or an icon before the value, e.g. a URL's domain in front of an editable slug. */
	startAdornment?: React.ReactNode;
	endAdornment?: React.ReactNode;
	multiline?: boolean;
	rows?: number;
	tooltip?: string;
	disabled?: boolean;
	/** Set false for code, which the browser would otherwise underline as misspelt. */
	spellCheck?: boolean;
	inputRef?: React.Ref<HTMLInputElement | HTMLTextAreaElement>;
	onKeyUp?: React.KeyboardEventHandler<HTMLDivElement>;
	onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
	autoFocus?: boolean;
}

const FormFieldComponent: React.FC<FormFieldProps> = ({
	id,
	label,
	hideLabel = false,
	value,
	onChange,
	onBlur,
	placeholder,
	required = false,
	error = false,
	helperText,
	hint,
	type = "text",
	min,
	max,
	startAdornment,
	endAdornment,
	multiline = false,
	rows,
	tooltip,
	disabled = false,
	spellCheck,
	inputRef,
	onKeyUp,
	onKeyDown,
	autoFocus,
	classes = {},
}) => {
	return (
		<Mui.FormControl fullWidth error={error} required={required}>
			<Mui.Typography
				component="label"
				htmlFor={id}
				className={hideLabel ? classes.visuallyHidden : classes.fieldLabel}
			>
				{tooltip ? <LabelWithTooltip label={label} tooltip={tooltip} /> : label}
				{required && <span className={classes.fieldLabelAsterisk}> *</span>}
			</Mui.Typography>
			<Mui.TextField
				id={id}
				inputRef={inputRef}
				onKeyUp={onKeyUp}
				onKeyDown={onKeyDown}
				autoFocus={autoFocus}
				fullWidth
				variant="outlined"
				value={value}
				onChange={onChange}
				onBlur={onBlur}
				placeholder={placeholder}
				type={type}
				disabled={disabled}
				error={error}
				helperText={helperText || hint}
				multiline={multiline}
				rows={rows}
				inputProps={spellCheck === undefined && min === undefined && max === undefined ? undefined : { spellCheck, min, max }}
				InputProps={{
					startAdornment: startAdornment ? (
						<Mui.InputAdornment position="start">{startAdornment}</Mui.InputAdornment>
					) : undefined,
					endAdornment: endAdornment ? (
						<Mui.InputAdornment position="end" className={classes.endAdornment}>
							{endAdornment}
						</Mui.InputAdornment>
					) : undefined,
					classes: {
						root: multiline ? classes.textFieldRootMultiline : classes.textFieldRoot,
						focused: classes.textFieldFocused,
						notchedOutline: classes.textFieldNotchedOutline,
						input: classes.textFieldInput,
						inputAdornedStart: classes.textFieldInputAdornedStart,
					},
				}}
				FormHelperTextProps={{
					classes: {
						root: helperText || !hint ? classes.helperText : classes.hint,
					},
				}}
			/>
		</Mui.FormControl>
	);
};

export const FormField = withStyles(FormFieldComponent, formFieldStyles);
